import React from 'react'
import Layout from '../components/layout2'
import Container from '../components/container'
import Vbox from '../components/vbox'
import Top from '../components/top'
import Summary from '../components/summary'
import Button from '../components/button'
import Img from '../components/img'
import ThreeColumn from '../components/three-column'
import ColumnCard from '../components/column-card'

class RootIndex extends React.Component {
  render() {
    return (
      <Layout>
        <Container>
          <Vbox widegap>
            <Top
              title="Design System for Vattenfall's B2B Portals"
              body="A project aimed at going from fragmented interfaces to a unified design standard. A design system that creates clarity, efficiency, and better user experiences."
              image="/portfolio/Vattenfall1.png"
            />
            <Summary>
              <h4 className="h4">Role</h4>
              <p className="small">UX Designer / Design System Designer</p>
              <h4 className="h4">Client</h4>
              <p className="small">Vattenfall B2B</p>
              <h4 className="h4">Assignment</h4>
              <p className="small">
                To create a foundational, token-based design system in Figma
                with accompanying documentation.
              </p>
              <h4 className="h4">Timeframe</h4>
              <p className="small">
                Startup and preliminary work during internship + 5 weeks Aug-Sep
                2025.
              </p>
              <h4 className="h4">Problems & Challenges</h4>
              <p className="small">
                - Inconsistent design and user experience within and between
                portals in Energy Site Suite.
                <br />
                - Many unique solutions and low design control.
                <br />- Vattenfall's official design system is adapted for the
                web and not for complex portals, so it cannot be used
              </p>
              <h4 className="h4">Result</h4>
              <p className="small">
                A foundational token-based design system and UI kit with
                documentation in Figma that is in use today.{' '}
              </p>
            </Summary>
            <div>
              <h2 className="h2">1 - Background: </h2>
              <Container narrow>
                <h3 className="h3">Vattenfall B2B</h3>
                <h4 className="h4">About Energy Site Suite</h4>
                <p className="body">
                  Vattenfall's B2B portals (collectively called Energy Site
                  Suite) are systems used by business customers, energy
                  companies, industrial customers, and internal traders to
                  manage power agreements, monitor consumption, buy and sell
                  electricity, make forecasts, and work with hedging strategies.
                </p>
                <p className="body">
                  These systems were developed by different teams without a
                  shared design system, which resulted in the interfaces varying
                  in both appearance and function. Each portal used different
                  components, colors, and patterns. This created inconsistency
                  in the user experience and made design and development
                  processes inefficient.
                </p>
                <p className="body">
                  As a UX designer, I was tasked with establishing the
                  foundation for a unified and scalable design system. The goal
                  was to create structure, simplify collaboration between teams,
                  and lay groundwork for future development of the portals.
                </p>
                <p className="body">
                  The project started during my internship in spring 2025 and
                  was then extended during fall 2025.
                </p>
              </Container>
            </div>

            <div>
              <h2 className="h2">2 - Results:</h2>
              <Container narrow>
                <h3 className="h3">Results & Deliverables</h3>
                <h4 className="h4">Token-based design system</h4>
                <p className="body">
                  Foundational structure for the design system with variables
                  and styles for colors, spacing, and typography that create
                  better control and scalability.
                </p>
                <h4 className="h4">Shared documentation</h4>
                <p className="body">
                  Documentation for both developers and designers gathered in
                  one place - at the overview level and at the component level.
                </p>
                <h4 className="h4">UI kit</h4>
                <p className="body">
                  Basic component library: icons, buttons, form elements, and
                  modules that are already being used.
                </p>
                <h4 className="h4">Collaboration</h4>
                <p className="body">
                  Started collaboration between portals - a shared direction,
                  and a first framework for how continued development can
                  proceed more uniformly.
                </p>
              </Container>
            </div>

            <div>
              <h2 className="h2">3 - Process:</h2>
              <Container narrow>
                <h3 className="h3">Audit, Identification & Prioritization</h3>
                <h4 className="h4">Mapping the current state</h4>
                <p className="body">
                  The project began with an audit of core components to identify
                  where the inconsistencies were most severe. By gathering
                  everything in a clear overview, I could visually demonstrate
                  to teams and stakeholders how fragmented the interfaces
                  actually were. It proved to be an effective way to create
                  alignment, explain the need for a design system, and
                  prioritize which components needed to be standardized first.
                </p>
                <Img
                  narrow
                  src="portfolio/Audit.png"
                  caption="Mapping of unique buttons and form elements - inconsistencies were found both within and between portals"
                />
              </Container>

              <Container narrow>
                <h3 className="h3">Workshops & Decision Making</h3>
                <h4 className="h4">Workshops</h4>
                <p className="body">
                  With insights from the audit, I facilitated several workshops
                  with designers and developers. The goal was to create
                  alignment around the current state, clarify needs, and make
                  collective decisions for the design system work.
                </p>
                <h4 className="h4">Decisions</h4>
                <p className="body">
                  During the workshops, we decided on components that were most
                  critical to standardize first, based on their frequency of use
                  and impact on user experience. We also decided on design
                  principles and guidelines that would guide our work going
                  forward.
                </p>
                <Img
                  narrow
                  src="portfolio/Workshop.png"
                  caption="Workshop with designers and developers"
                />
              </Container>

              <Container narrow>
                <h3 className="h3">Design System Structure</h3>
                <h4 className="h4">Structure and documentation</h4>
                <p className="body">
                  An initial structure for the UI kit has been developed where
                  core components, modules, and guidelines are gathered in one
                  place. The purpose is to create a shared foundation for
                  designers and developers, where everything from typography and
                  colors to buttons, forms, and modules is documented, visually
                  consistent, and adapted for accessibility.
                </p>
                <p className="body">
                  By gathering and visualizing available components, it becomes
                  easier to standardize, reuse, and further develop the
                  interface in a unified way. The shared structure makes it
                  clearer how, for example, colors, states, and interactions
                  should function and be used to meet WCAG requirements.
                </p>
                <p className="body">
                  Since developers work in different codebases and languages,
                  Figma is currently serving as the shared place where
                  everything can be documented and shared - a natural first step
                  before the design system is moved into code.
                </p>
                <Img
                  narrow
                  src="portfolio/structureDesignsystem.png"
                  caption="Mapping of unique buttons and form elements - inconsistencies were found both within and between portals"
                />
                <h4 className="h4">Component building</h4>
                <p className="body">
                  All components are built to be flexible so they can adapt to
                  different needs and scenarios according to standard
                  principles. The design builds on the official design system
                  but is adapted for the portals.
                </p>
                <Img
                  narrow
                  src="portfolio/structureComponent.png"
                  caption="Example of components in the UI kit"
                />

                <h4 className="h4">Design Tokens and Components</h4>
                <p className="body">
                  While working on the design system, we discovered that
                  variations in colors, unclear states, and inconsistent
                  components were often due to a lack of foundational structure.
                  That's why we introduced a token-based model where colors,
                  typography, and spacing are defined centrally and used
                  consistently throughout the library.
                </p>
                <p className="body">
                  This not only gave us a more unified expression, but also
                  better control over how colors and components are used, which
                  in turn made it easier to ensure that accessibility
                  requirements, such as contrast levels, are met. By handling
                  updates at the token level, they spread in a controlled way to
                  all components, which simplifies maintenance and makes the
                  system both more scalable and more robust over time.
                </p>
                <Img
                  narrow
                  src="portfolio/structureTokens.png"
                  caption="Visualization of the relationship between color tokens, styles, and how they are applied in different error components."
                />
              </Container>

              <Container narrow>
                <h3 className="h3">Testing & Quality Assurance</h3>
                <h4 className="h4">
                  Approach to testing and quality in the design system
                </h4>
                <p className="body">
                  While building components and tokens, we tested in parallel to
                  ensure the system held up in practice with realistic
                  scenarios. We established an iterative approach to testing and
                  quality assurance, where components are created, tested, and
                  adjusted in multiple cycles. The work involved continuous
                  alignment around naming, implementation, and improvements - to
                  ensure the system works both in design and code, and in real
                  user contexts.
                </p>

                <ThreeColumn>
                  <ColumnCard
                    image="/portfolio/test1.png"
                    title="Testing in context"
                    body="Testing in realistic scenarios and user contexts"
                  />
                  <ColumnCard
                    image="/portfolio/test2.png"
                    title="Dev + Design collaboration"
                    body="Alignment on naming, documentation, and implementation"
                  />
                  <ColumnCard
                    image="/portfolio/test3.png"
                    title="Iterative improvement"
                    body="Adjustments based on feedback and contextual testing"
                  />
                </ThreeColumn>
              </Container>
            </div>

            <div>
              <h2 className="h2">4 - Summary:</h2>
              <Container narrow>
                <p className="body">
                  <i>
                    "The goal is not to rebuild everything, but to have a shared
                    truth to start from when we build new things"
                  </i>{' '}
                  - shared guiding principle
                </p>
                <p className="body">
                  By establishing a scalable and living design system, I created
                  the conditions for a long-term and more sustainable way of
                  working. Now there is a shared design foundation that can be
                  developed, improved, and built upon over time.
                </p>
                <h3 className="h3">Insights & Learnings</h3>
                <h4 className="h4">Workshops as a method and tool</h4>
                <p className="body">
                  It was challenging to take the leap, but I've really seen how
                  effective workshops are for decision-making. They provide
                  structured opportunities for discussion, solution exploration,
                  alignment, and engagement. I used different formats: a
                  half-day on-site but also shorter, focused meetings, adapted
                  based on time and problem type.
                </p>
                <h4 className="h4">Continuously share insights</h4>
                <p className="body">
                  By creating and sharing the audit early in the process, I
                  gained a deeper understanding of the problems and could
                  clearly see where the inconsistencies were most severe. I
                  realized that I had previously assumed everyone already had a
                  clear picture of the current state, but although many
                  experienced the portals as fragmented, the extent only became
                  clear when visualized in black and white. Concretely showing
                  the inconsistencies created both engagement and a shared
                  understanding among stakeholders and teams - a sense of "now
                  we're tackling this together".
                </p>
                <h4 className="h4">Build accessibility from the start</h4>
                <p className="body">
                  By, among other things, defining colors at the token level and
                  naming them by use case rather than appearance, we ensure
                  contrast requirements from the start and make it easier for
                  the team to use the right colors at the right time. This
                  reduces the risk of misuse and makes it easier to uphold
                  accessibility standards over time.
                </p>
                <h4 className="h4">
                  More time and tighter collaboration with developers
                </h4>
                <p className="body">
                  I had regular syncs with developers, but tighter and more
                  continuous collaboration from the start would have made it
                  easier to find common ground, make more decisions earlier,
                  strengthen quality, and create a stronger shared foundation.
                </p>
              </Container>
            </div>

            <div>
              <h2 className="h2">More Projects:</h2>
              <div className="more-wrapper">
                <div class="more">
                  <img src="/jhl.png" />
                  <h3 className="h3">Jagharlast.se</h3>
                  <Button
                    to="/jagharlast"
                    ariaLabel="Explore the full project for Jagharlast.se"
                  >
                    Explore the full project
                  </Button>
                </div>
                <div class="more">
                  <img src="/SpeedEats1.png" />
                  <h3 className="h3">SpeedEats</h3>
                  <Button
                    to="/speedeats"
                    ariaLabel="Explore the full project for SpeedEats"
                  >
                    Explore the full project
                  </Button>
                </div>
              </div>
            </div>
          </Vbox>
        </Container>
      </Layout>
    )
  }
}

export default RootIndex
